import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

const PAID_BY_LABEL = { customer: "Customer pays", included: "Included in ticket price" };

// Sprint AT — Service fee & platform-wide Service-fee GST.
// Option (c): this card renders for ADMIN only. Admins have full view + edit of
// "Service Fee Paid By". Values prefer the edited event's stored snapshot
// (eventFeeConfig) and fall back to the platform/admin defaults on create.
export function PlatformFeesSection({ scPaidBy, onChange, locked = false, isAdmin = false, eventFeeConfig = null }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api
      .get("/organisers/me/fee-settings")
      .then((r) => setCfg(r.data))
      .catch(() => setCfg({}));
  }, []);

  const ev = eventFeeConfig || {};
  const scVal = scPaidBy || ev.service_charge_paid_by || cfg?.service_charge_paid_by || "customer";
  const platformGstOn =
    ev.gst_enabled != null ? ev.gst_enabled !== false : (cfg ? cfg.service_fee_gst_enabled !== false : true);
  // Admin always retains edit of the paid-by choice; organiser flag path kept for safety.
  const canEditSc = isAdmin ? true : (!locked && cfg?.allow_organiser_service_charge_paid_by_choice !== false);
  const scRate =
    ev.service_charge_rate_bps != null ? ev.service_charge_rate_bps / 100 : (cfg?.service_charge_percent ?? 2.5);
  const scFlatCents =
    ev.service_charge_per_ticket_cents != null
      ? ev.service_charge_per_ticket_cents
      : (cfg?.service_charge_per_ticket_cents ?? 30);
  const scFlat = (scFlatCents / 100).toFixed(2);
  const gstRate =
    ev.gst_rate_bps != null ? ev.gst_rate_bps / 100 : (cfg?.gst_rate_bps != null ? cfg.gst_rate_bps / 100 : 10);

  return (
    <div className="gt-card" data-testid="platform-fees-section">
      <div className="gt-label mb-4">04 · Fees &amp; GST</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
        <div>
          <div className="gt-form-label">Service Fee</div>
          <div className="font-mono font-bold" data-testid="fees-service-charge-rate">
            {scRate}% + A${scFlat} per-ticket fee
          </div>
          {canEditSc ? (
            <>
              <label className="gt-form-label mt-3">Service Fee Paid By</label>
              <select
                className="gt-input"
                value={scVal}
                onChange={(e) => onChange?.("service_charge_paid_by", e.target.value)}
                data-testid="event-sc-paid-by-select"
              >
                <option value="customer">Customer pays</option>
                <option value="included">Organiser absorbs / included in ticket price</option>
              </select>
            </>
          ) : (
            <div className="text-xs text-[var(--gt-ink-2)] mt-2">
              Paid by:{" "}
              <span className="font-semibold" data-testid="fees-service-charge-paid-by">
                {PAID_BY_LABEL[scVal]}
              </span>
              {!locked && (
                <div className="text-[10px] text-[var(--gt-ink-3)] mt-1" data-testid="fees-sc-admin-managed-note">
                  Managed by Getickets Admin
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          <div className="gt-form-label">Service fee GST</div>
          <div className="font-mono font-bold" data-testid="fees-tax-rate">
            {platformGstOn ? `${gstRate}% of the service fee` : "Not applied"}
          </div>
          <div className="text-xs text-[var(--gt-ink-2)] mt-2" data-testid="fees-gst-platform-note">
            Set platform-wide by Getickets — GST applies to the service fee only and follows the
            Service fee paid-by setting.
          </div>
        </div>
      </div>
      {locked ? (
        <p className="text-xs font-semibold text-[var(--gt-accent)] mt-4" data-testid="fees-locked-note">
          {isAdmin
            ? "This event has paid orders — admin changes to payment settings require a reason and are audit-logged."
            : "Locked — this event has paid orders. Contact Getickets Admin to change payment settings."}
        </p>
      ) : (
        <p className="text-xs text-[var(--gt-ink-3)] mt-4" data-testid="fees-managed-note">
          Service fee rates and Service fee GST are managed platform-wide by Getickets Admin.
        </p>
      )}
    </div>
  );
}
